import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Grid,
  Box,
  Chip,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { diffWords } from 'diff';

interface ComparisonResultDialogProps {
  open: boolean;
  onClose: () => void;
  version1: any;
  version2: any;
}

const ArrayDiffViewer: React.FC<{ oldArray: any[]; newArray: any[] | undefined; type: 'new' | 'old' }> = ({ oldArray = [], newArray = [], type }) => {
  const theme = useTheme();
  const oldSet = new Set(oldArray);
  const newSet = new Set(newArray);

  const arrayToRender = type === 'old' ? oldArray : newArray;

  return (
    <Box sx={{ p: 1 }}>
      {arrayToRender.map((item, index) => {
        let chipStyle: any = { m: 0.5 };

        if (type === 'old' && !newSet.has(item)) { // removed
          chipStyle = { ...chipStyle, backgroundColor: alpha(theme.palette.error.main, 0.2), textDecoration: 'line-through' };
        } else if (type === 'new' && !oldSet.has(item)) { // added
          chipStyle = { ...chipStyle, backgroundColor: alpha(theme.palette.success.main, 0.2) };
        }
        
        return <Chip key={index} label={item} sx={chipStyle} />;
      })}
    </Box>
  );
};

const DiffViewer: React.FC<{ oldText: string; newText: string; type: 'new' | 'old' }> = ({ oldText, newText, type }) => {
  const theme = useTheme();
  const differences = diffWords(String(oldText === 'null' ? '' : oldText), String(newText === 'null' ? '' : newText));

  return (
    <Typography component="div" sx={{ p: 1, whiteSpace: 'pre-wrap' }}>
      {differences.map((part, index) => {
        if (type === 'old' && part.added) {
          return null;
        }
        if (type === 'new' && part.removed) {
          return null;
        }

        const style: React.CSSProperties = {
          backgroundColor: 'transparent',
        };

        if (part.added) {
          style.backgroundColor = alpha(theme.palette.success.main, 0.2);
        } else if (part.removed) {
          style.backgroundColor = alpha(theme.palette.error.main, 0.2);
          style.textDecoration = 'line-through';
        }

        return (
          <span key={index} style={style}>
            {part.value}
          </span>
        );
      })}
    </Typography>
  );
};

const ComparisonResultDialog: React.FC<ComparisonResultDialogProps> = ({ open, onClose, version1, version2 }) => {
  const theme = useTheme();

  const [v1, v2] = React.useMemo(() => {
    if (!version1 || !version2) return [null, null];
    return [version1, version2].sort((a, b) => (a.version_number || 0) - (b.version_number || 0));
  }, [version1, version2]);

  const thesisData1 = v1?.thesis_data || {};
  const thesisData2 = v2?.thesis_data || {};

  const allKeys = Array.from(new Set([...Object.keys(thesisData1), ...Object.keys(thesisData2)]));
  const filteredKeys = allKeys.filter(key => !['fund_id', 'is_global', 'firm_profile_id', 'id', 'created_at', 'version'].includes(key));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Version Comparison</DialogTitle>
      <DialogContent>
        {v1 && v2 ? (
          <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}` }}>
            <Grid container>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" fontWeight="bold" pb={1} borderBottom={1} borderColor="divider">
                  <Typography variant="subtitle1" sx={{ flex: 1 }}>Field</Typography>
                  <Typography variant="subtitle1" sx={{ flex: 2 }}>Version {v1.version_number}</Typography>
                  <Typography variant="subtitle1" sx={{ flex: 2 }}>Version {v2.version_number}</Typography>
                </Box>
              </Grid>
              {filteredKeys.map((key, index) => {
                const oldValue = thesisData1[key];
                const newValue = thesisData2[key];
                const hasChanged = JSON.stringify(oldValue) !== JSON.stringify(newValue);

                return (
                  <Grid item xs={12} key={index}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="stretch"
                      py={1}
                      borderBottom={1}
                      borderColor="divider"
                      sx={{
                        backgroundColor: hasChanged ? alpha(theme.palette.info.light, 0.1) : 'transparent',
                      }}
                    >
                      <Box sx={{ flex: 1, fontWeight: 'bold', pr: 2, py: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 2, pr: 1, borderRight: `1px solid ${theme.palette.divider}` }}>
                        {Array.isArray(oldValue) ? (
                          <ArrayDiffViewer oldArray={oldValue} newArray={newValue} type="old" />
                        ) : (
                          <DiffViewer oldText={String(oldValue)} newText={String(newValue)} type="old" />
                        )}
                      </Box>
                      <Box sx={{ flex: 2, pl: 1 }}>
                        {Array.isArray(newValue) ? (
                          <ArrayDiffViewer oldArray={oldValue} newArray={newValue} type="new" />
                        ) : (
                          <DiffViewer oldText={String(oldValue)} newText={String(newValue)} type="new" />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        ) : (
          <Typography>No comparison data available.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComparisonResultDialog;